/**
 * Internes Telefonbuch (Phonebook).
 *
 * Liefert dienstliche Kontaktdaten aller aktiven Mitarbeiter,
 * gruppiert nach Niederlassung -> Standort. Sichtbar fuer alle
 * eingeloggten Nutzer.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useOrgLabels } from '../contexts/OrgLabelsContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PositionBadges } from './employees/PositionBadges';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Phone, Smartphone, Mail, Search, Printer, Download, Building2, MapPin, Users, Loader2,
} from 'lucide-react';
import api, { branchesAPI, locationsAPI } from '../lib/api';
import { toast } from 'sonner';
import PhonebookEmployeeDetail from './PhonebookEmployeeDetail';
import './Phonebook.print.css';

const ContactLink = ({ icon: Icon, value, type }) => {
  if (!value) return <span className="text-muted-foreground/50">—</span>;
  const href = type === 'mail' ? `mailto:${value}` : `tel:${value.replace(/\s+/g, '')}`;
  return (
    <a
      href={href}
      className="inline-flex items-center gap-1.5 text-sm hover:text-primary hover:underline"
      data-testid={`phonebook-${type}-link`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="break-all">{value}</span>
    </a>
  );
};

export default function Phonebook() {
  const orgL = useOrgLabels();
  const [data, setData] = useState({ branches: [], total_employees: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Excel-Export Dialog
  const [exportOpen, setExportOpen] = useState(false);
  const [exportScope, setExportScope] = useState('company');
  const [exportScopeId, setExportScopeId] = useState('');
  const [exportLayout, setExportLayout] = useState('per_location');
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [exporting, setExporting] = useState(false);

  // Kontakt-Karte Modal
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    api.get('/phonebook')
      .then((res) => setData(res.data || { branches: [], total_employees: 0 }))
      .catch(() => toast.error('Telefonbuch konnte nicht geladen werden'))
      .finally(() => setLoading(false));
    branchesAPI.getAll().then((r) => setBranches(r.data || [])).catch(() => {});
    locationsAPI.getAll().then((r) => setLocations(r.data || [])).catch(() => {});
  }, []);

  // Filter (clientseitig) ueber Such-Token
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    const tokens = q.split(/\s+/).filter(Boolean);
    const matches = (text) => {
      const t = (text || '').toLowerCase();
      return tokens.every((tok) => t.includes(tok));
    };
    const newBranches = [];
    let total = 0;
    for (const br of data.branches) {
      const newLocs = [];
      for (const loc of br.locations) {
        const filteredEmps = loc.employees.filter((e) =>
          matches(`${e.name} ${e.phone} ${e.mobile} ${e.email} ${e.position} ${e.department} ${loc.name} ${br.name}`)
        );
        if (filteredEmps.length) {
          newLocs.push({ ...loc, employees: filteredEmps });
          total += filteredEmps.length;
        }
      }
      if (newLocs.length) newBranches.push({ ...br, locations: newLocs });
    }
    return { branches: newBranches, total_employees: total };
  }, [data, search]);

  const handleExport = async () => {
    if (exportScope !== 'company' && !exportScopeId) {
      toast.error(exportScope === 'branch' ? `Bitte ${orgL.branch} wählen` : `Bitte ${orgL.location} wählen`);
      return;
    }
    setExporting(true);
    try {
      const params = { scope: exportScope, layout: exportLayout, format: exportFormat };
      if (exportScope !== 'company') params.id = exportScopeId;
      const res = await api.get('/phonebook/export', { params, responseType: 'blob' });
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      const fallbackExt = exportFormat === 'outlook_csv' ? 'csv' : exportFormat === 'email_list' ? 'txt' : 'xlsx';
      const fileName = match?.[1] || `Telefonbuch.${fallbackExt}`;
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(
        exportFormat === 'email_list'
          ? 'E-Mail-Liste heruntergeladen'
          : exportFormat === 'outlook_csv'
            ? 'Outlook-Kontakte-CSV heruntergeladen'
            : 'Telefonbuch heruntergeladen',
      );
      setExportOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 phonebook-root" data-testid="phonebook-page">
        {/* Header (nicht im Druck) */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 print-hide">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl">Telefonbuch</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Internes Verzeichnis dienstlicher Kontaktdaten · {filtered.total_employees} Einträge
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => window.print()}
              data-testid="phonebook-print-btn"
              className="rounded-full"
            >
              <Printer className="mr-2 h-4 w-4" />
              Drucken / PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => setExportOpen(true)}
              data-testid="phonebook-export-btn"
              className="rounded-full"
            >
              <Download className="mr-2 h-4 w-4 text-purple-600" />
              Exportieren
            </Button>
          </div>
        </div>

        {/* Suche */}
        <div className="relative max-w-md print-hide">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Suche: Name, Standort, Funktion, Abteilung…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="phonebook-search"
          />
        </div>

        {/* Print-Header (nur im Druck sichtbar) */}
        <div className="hidden print-only">
          <h2 className="text-2xl font-bold">Telefonbuch</h2>
          <p className="text-sm">Stand: {new Date().toLocaleDateString('de-DE')}</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Telefonbuch…
          </div>
        ) : filtered.branches.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-muted-foreground">
              <Users className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>Keine Einträge gefunden.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {filtered.branches.map((branch) => (
              <section
                key={branch.id || 'no-branch'}
                className="space-y-3 phonebook-branch"
                data-testid={`phonebook-branch-${branch.id || 'none'}`}
              >
                {/* Niederlassungs-Header */}
                <div className="flex flex-wrap items-center gap-2 border-b border-border/40 pb-2 phonebook-branch-header">
                  <Building2 className="h-5 w-5 text-purple-600 shrink-0" />
                  <h3 className="font-heading text-lg sm:text-xl">{branch.name}</h3>
                  {(branch.phone || branch.email || branch.fax) && (
                    <span className="text-xs text-muted-foreground sm:ml-auto break-all">
                      {branch.phone && <>Tel: {branch.phone}</>}
                      {branch.fax && <> · Fax: {branch.fax}</>}
                      {branch.email && <> · {branch.email}</>}
                    </span>
                  )}
                </div>

                {/* Standorte */}
                <div className="space-y-3">
                  {branch.locations.map((loc) => (
                    <Card
                      key={loc.id}
                      id={`location-${loc.id}`}
                      className="border-border/60 phonebook-location scroll-mt-20"
                      data-testid={`phonebook-location-${loc.id}`}
                    >
                      <CardHeader className="border-b border-border/40 pb-3">
                        <div className="flex flex-wrap items-baseline gap-2">
                          <MapPin className="h-4 w-4 text-primary" />
                          <CardTitle className="text-base font-heading">{loc.name}</CardTitle>
                          {loc.city && (
                            <span className="text-xs text-muted-foreground">· {loc.city}</span>
                          )}
                          <Badge variant="secondary" className="ml-auto text-[10px]">
                            {loc.employees.length} {loc.employees.length === 1 ? 'Person' : 'Personen'}
                          </Badge>
                        </div>
                        {(loc.phone || loc.fax || loc.email) && (
                          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground pt-1.5">
                            {loc.phone && (
                              <span className="inline-flex items-center gap-1 break-all">
                                <Phone className="h-3 w-3 shrink-0" /> {loc.phone}
                              </span>
                            )}
                            {loc.fax && (
                              <span className="inline-flex items-center gap-1 break-all">Fax: {loc.fax}</span>
                            )}
                            {loc.email && (
                              <span className="inline-flex items-center gap-1 break-all">
                                <Mail className="h-3 w-3 shrink-0" /> {loc.email}
                              </span>
                            )}
                          </div>
                        )}
                      </CardHeader>
                      <CardContent className="p-0">
                        {/* Mobile: Karten-Layout (< md) */}
                        <ul className="md:hidden divide-y divide-border/30">
                          {loc.employees.map((emp) => (
                            <li
                              key={emp.id}
                              className="p-3"
                              data-testid={`phonebook-row-${emp.id}`}
                            >
                              <button
                                type="button"
                                onClick={() => setDetailId(emp.id)}
                                className="text-left w-full"
                                data-testid={`phonebook-name-btn-${emp.id}`}
                              >
                                <div className="font-medium hover:text-primary hover:underline">{emp.name}</div>
                                {(emp.position || (emp.position_list && emp.position_list.length > 0) || emp.department) && (
                                  <div className="text-[11px] text-muted-foreground mt-0.5 break-words flex flex-wrap items-center gap-1">
                                    <PositionBadges employee={emp} />
                                    {(emp.position || (emp.position_list && emp.position_list.length > 0)) && emp.department && <span>·</span>}
                                    {emp.department && <span>{emp.department}</span>}
                                  </div>
                                )}
                              </button>
                              {/* Kontakt-Icons unten */}
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {emp.phone && (
                                  <a
                                    href={`tel:${emp.phone.replace(/\s+/g, '')}`}
                                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:border-primary hover:text-primary"
                                    data-testid={`phonebook-mobile-phone-${emp.id}`}
                                  >
                                    <Phone className="h-3 w-3" />
                                    <span className="break-all">{emp.phone}</span>
                                  </a>
                                )}
                                {emp.mobile && (
                                  <a
                                    href={`tel:${emp.mobile.replace(/\s+/g, '')}`}
                                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:border-primary hover:text-primary"
                                    data-testid={`phonebook-mobile-mobile-${emp.id}`}
                                  >
                                    <Smartphone className="h-3 w-3" />
                                    <span className="break-all">{emp.mobile}</span>
                                  </a>
                                )}
                                {emp.email && (
                                  <a
                                    href={`mailto:${emp.email}`}
                                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:border-primary hover:text-primary max-w-full"
                                    data-testid={`phonebook-mobile-email-${emp.id}`}
                                  >
                                    <Mail className="h-3 w-3 shrink-0" />
                                    <span className="break-all truncate">{emp.email}</span>
                                  </a>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>

                        {/* Desktop: Tabelle (>= md) */}
                        <div className="hidden md:block overflow-x-auto">
                          <table className="w-full text-sm phonebook-table table-fixed">
                            <colgroup>
                              <col className="w-[18%]" />
                              <col className="w-[14%]" />
                              <col className="w-[14%]" />
                              <col className="w-[22%]" />
                              <col className="w-[16%]" />
                              <col className="w-[16%]" />
                            </colgroup>
                            <thead className="bg-muted/40 text-xs text-muted-foreground">
                              <tr>
                                <th className="text-left px-3 py-2 font-medium">Name</th>
                                <th className="text-left px-3 py-2 font-medium">Festnetz</th>
                                <th className="text-left px-3 py-2 font-medium">Mobil</th>
                                <th className="text-left px-3 py-2 font-medium">E-Mail</th>
                                <th className="text-left px-3 py-2 font-medium">Funktion</th>
                                <th className="text-left px-3 py-2 font-medium">Maßnahme/Abteilung</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loc.employees.map((emp) => (
                                <tr
                                  key={emp.id}
                                  className="border-t border-border/30 hover:bg-muted/20 align-top"
                                  data-testid={`phonebook-row-${emp.id}-desktop`}
                                >
                                  <td className="px-3 py-2 font-medium break-words">
                                    <button
                                      type="button"
                                      onClick={() => setDetailId(emp.id)}
                                      className="hover:text-primary hover:underline text-left"
                                    >
                                      {emp.name}
                                    </button>
                                  </td>
                                  <td className="px-3 py-2 break-all"><ContactLink icon={Phone} value={emp.phone} type="phone" /></td>
                                  <td className="px-3 py-2 break-all"><ContactLink icon={Smartphone} value={emp.mobile} type="phone" /></td>
                                  <td className="px-3 py-2 break-all"><ContactLink icon={Mail} value={emp.email} type="mail" /></td>
                                  <td className="px-3 py-2 text-muted-foreground break-words"><PositionBadges employee={emp} /></td>
                                  <td className="px-3 py-2 text-muted-foreground break-words">{emp.department || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Excel-Export-Dialog */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent data-testid="phonebook-export-dialog" className="p-4 sm:p-6 gap-3 sm:gap-4">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Telefonbuch exportieren</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Wähle Format und Bereich. Personen ohne dienstliches Telefon, Mobil oder E-Mail werden ausgeblendet.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1 sm:py-2">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Format</label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger data-testid="phonebook-export-format" className="h-auto min-h-10 whitespace-normal text-left [&>span]:whitespace-normal [&>span]:line-clamp-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-2.5rem)]">
                  <SelectItem value="xlsx" className="whitespace-normal">Excel-Telefonbuch (.xlsx)</SelectItem>
                  <SelectItem value="outlook_csv" className="whitespace-normal">Outlook-Kontakte (.csv) – nur dienstliche E-Mails</SelectItem>
                  <SelectItem value="email_list" className="whitespace-normal">E-Mail-Liste (.txt, „;"-getrennt fürs An-/BCC-Feld)</SelectItem>
                </SelectContent>
              </Select>
              {exportFormat === 'outlook_csv' && (
                <p className="text-xs text-muted-foreground pt-1">
                  In Outlook: Datei → Öffnen und Exportieren → Importieren/Exportieren → „Aus anderem Programm importieren" → Kommagetrennte Werte → diese CSV wählen.
                </p>
              )}
              {exportFormat === 'email_list' && (
                <p className="text-xs text-muted-foreground pt-1">
                  Inhalt der .txt einfach kopieren und in das An- oder BCC-Feld einer neuen Outlook-Mail einfügen.
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Bereich</label>
              <Select value={exportScope} onValueChange={(v) => { setExportScope(v); setExportScopeId(''); }}>
                <SelectTrigger data-testid="phonebook-export-scope">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Gesamte Firma</SelectItem>
                  <SelectItem value="branch">{orgL.branch}</SelectItem>
                  <SelectItem value="location">{orgL.location}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {exportScope !== 'company' && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  {exportScope === 'branch' ? orgL.branch : orgL.location}
                </label>
                <Select value={exportScopeId} onValueChange={setExportScopeId}>
                  <SelectTrigger data-testid="phonebook-export-scope-id">
                    <SelectValue placeholder={exportScope === 'branch' ? `${orgL.branch} wählen…` : `${orgL.location} wählen…`} />
                  </SelectTrigger>
                  <SelectContent>
                    {(exportScope === 'branch' ? branches : locations).map((it) => (
                      <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Layout</label>
              <Select value={exportLayout} onValueChange={setExportLayout} disabled={exportFormat !== 'xlsx'}>
                <SelectTrigger data-testid="phonebook-export-layout" className="h-auto min-h-10 whitespace-normal text-left [&>span]:whitespace-normal [&>span]:line-clamp-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-2.5rem)]">
                  <SelectItem value="per_location" className="whitespace-normal">Ein Sheet pro {orgL.location} (wie Vorlage)</SelectItem>
                  <SelectItem value="single_sheet" className="whitespace-normal">Ein Sheet mit {orgL.location}-Spalte (kompakt, filterbar)</SelectItem>
                </SelectContent>
              </Select>
              {exportFormat !== 'xlsx' && (
                <p className="text-xs text-muted-foreground pt-1">Layout gilt nur für den Excel-Export.</p>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" className="w-full sm:w-auto" onClick={() => setExportOpen(false)}>Abbrechen</Button>
            <Button
              className="w-full sm:w-auto"
              onClick={handleExport}
              disabled={exporting}
              data-testid="phonebook-export-confirm"
            >
              {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              Herunterladen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kontakt-Karte (Detail-Modal) */}
      <PhonebookEmployeeDetail
        employeeId={detailId}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        onJumpToLocation={(locId) => {
          if (!locId) return;
          // sanftes Scrollen zum Standort
          setTimeout(() => {
            const el = document.getElementById(`location-${locId}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }}
      />
    </DashboardLayout>
  );
}
